/**
 * Email editor report API client + labels.
 *
 * Wraps GET /v1/admin-review/email-editor (owner/admin-gated server-side
 * via requireCapability('manage_team'); the rail visibility is convenience,
 * not security). Mirrors src/lib/aiUsage.ts.
 *
 * The report is counts only: how many email drafts went through the
 * fidelity editor, how many it edited, how many were served unedited and
 * why, what kinds of thing it cut, and how long its call took. The words
 * it cut are email text and stay on the audit rows.
 */

import { WINDOW_OPTIONS, type WindowDays } from './aiUsage';
import { apiErrorFrom } from './api';

export { WINDOW_OPTIONS, type WindowDays };

export interface EmailEditorDay {
  day: string;
  drafts: number;
  applied: number;
  unedited: number;
  changes_total: number;
  claims_total: number;
  mean_duration_ms: number | null;
}

export interface EmailEditorUser {
  user_id: string;
  user_display_name: string | null;
  user_email: string | null;
  drafts: number;
  applied: number;
  unedited: number;
  changes_total: number;
}

export interface EmailEditorReport {
  window_days: number;
  time_zone: string;
  totals: {
    drafts: number;
    applied: number;
    unedited: number;
    unedited_by_error: Record<string, number>;
    editor_error_rows: Record<string, number>;
    editor_ok_rows: number;
    changes_total: number;
    claims_total: number;
    mean_duration_ms: number | null;
  };
  by_day: EmailEditorDay[];
  by_user: EmailEditorUser[];
  cut_kinds: Record<string, number>;
  request_id?: string;
}

/** The kinds of cut the editor's pass classifies (api/_shared/aiTools/cutKinds.ts), in display order. */
export const CUT_KIND_LABELS: ReadonlyArray<[string, string]> = [
  ['about_the_email', 'A sentence about the email itself'],
  ['said', 'What someone said or did, beyond the notes'],
  ['reader', 'A claim about what the reader feels or knows'],
  ['reassurance', 'A reassurance or softening the notes do not hold'],
  ['commitment', 'A commitment the notes do not hold'],
  ['cause', 'A cause or reason the notes do not give'],
  ['manner', 'How the client seemed, beyond the notes'],
  ['thank_you', 'A thank-you for nothing the notes show'],
  ['suggestion', 'A suggestion to the reader'],
  ['who', 'Who did what, changed'],
  ['time', 'A time or date the notes do not give'],
  ['name', 'A name or title the notes do not give'],
  ['number', 'A number the notes do not give'],
  ['other', 'Other'],
];

export function cutKindLabel(kind: string): string {
  return CUT_KIND_LABELS.find(([k]) => k === kind)?.[1] ?? kind;
}

/** Why a draft was served unedited, in plain words. */
export function uneditedReasonLabel(code: string): string {
  switch (code) {
    case 'anthropic_call_failed':
      return 'The editor could not be reached';
    case 'anthropic_invalid_json':
      return "The editor's answer was not an edit, twice";
    case 'anthropic_max_tokens':
      return "The editor's answer was cut short";
    case 'editor_body_rejected':
      return "The editor's body lost the greeting or most of the draft, twice";
    case 'anthropic_refusal':
      return 'The editor declined the request';
    case 'unknown':
      return 'No reason recorded';
    default:
      return code;
  }
}

/** The editor's own error rows, by the code the pass wrote. */
export function editorErrorLabel(code: string): string {
  switch (code) {
    case 'invalid_body':
      return 'Answer was not an edit';
    case 'max_tokens':
      return 'Answer cut short';
    case 'edited_body_rejected':
      return 'Body refused by the gate';
    case 'refusal':
      return 'Declined the request';
    case 'call_failed':
      return 'Call failed';
    default:
      return code;
  }
}

const base = (slug: string): string =>
  `/api/t/${encodeURIComponent(slug)}/v1/admin-review/email-editor`;

export const emailEditorApi = {
  async report(tenantSlug: string, windowDays: WindowDays, signal?: AbortSignal): Promise<EmailEditorReport> {
    const r = await fetch(`${base(tenantSlug)}?days=${windowDays}`, {
      headers: { 'content-type': 'application/json' },
      signal,
    });
    if (!r.ok) throw await apiErrorFrom(r, `GET ${base(tenantSlug)}?days=${windowDays}`);
    return (await r.json()) as EmailEditorReport;
  },
};
